import { passkeyError } from '../../auth/passkeys'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button, Input, buttonActionIcon } from '../ui'
import { useEffect, useMemo, useState } from 'react'
import { InputOTP } from '../ui'
import { toast } from 'sonner'
import QRCode from 'react-qr-code'
import { Copy, Eye, EyeOff } from 'lucide-react'
import type { AdminUIRole, LoginProps } from '../../types/auth'
import { Modal } from '../ui/modal'
import { Header, SubHeader } from '../ui/header'
import { cn } from '../../lib/utils'
import { logo } from '../../components/ui/logo'
import { LoadingPage } from '../loading/pages'

export function Login<Role extends string = AdminUIRole>({
  authInstance,
  authProvider,
  onLoginSuccess,
  onAdminLoginSuccess,
  variant = 'user',
}: LoginProps<Role>) {
  const navigate = useNavigate()
  const location = useLocation()

  const [callState, setCallState] = useState<
    'success' | 'error' | 'loading' | 'action' | 'disabled'
  >('disabled')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [mfaNext, setMfaNext] = useState(false)
  const [open, setOpen] = useState(false)
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false)
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null)
  const [mfaLoading, setMfaLoading] = useState(false)
  const [mfaCode, setMfaCode] = useState('')
  const [mfaError, setMfaError] = useState(false)
  const [isSuperAdminFlow, setIsSuperAdminFlow] = useState(false)
  const [passkeyAvailable, setPasskeyAvailable] = useState<boolean | null>(null)
  const loginComplete = !!authProvider.auth || callState === 'success'

  // Reset superadmin flow if credentials change after it was triggered
  useEffect(() => {
    setIsSuperAdminFlow(false)
  }, [username, password])

  useEffect(() => {
    let current = true
    setPasskeyAvailable(null)
    if (open && !loginComplete && !isSuperAdminFlow && authInstance.loginWithPasskey && authInstance.passkeyLoginAvailable) {
      void authInstance.passkeyLoginAvailable(username, password)
        .then((available) => { if (current) setPasskeyAvailable(available) })
        .catch(() => { if (current) setPasskeyAvailable(false) })
    }
    return () => { current = false }
  }, [authInstance, loginComplete, open, isSuperAdminFlow, username, password])

  useEffect(() => {
    if (authProvider.auth) {
      if (onLoginSuccess) {
        onLoginSuccess()
      } else {
        navigate('/', { replace: true })
      }
    }
  }, [authProvider.auth, navigate, location.state, onLoginSuccess])

  const handleSubmit = async (secret?: string) => {
    if (username.length === 0 || password.length === 0) {
      toast.error('Please enter a username and password')
      setCallState('disabled')
      return
    }

    // If we already identified this as a superadmin flow, go straight to adminLogin
    if (isSuperAdminFlow && onAdminLoginSuccess && authInstance.adminLogin) {
      try {
        setCallState('loading')
        const adminData = await authInstance.adminLogin(username, password, secret)
        setCallState('success')
        onAdminLoginSuccess(adminData.token)
      } catch (e) {
        const errorMessage = (e as Error).message
        if (errorMessage === 'MFA required') {
          setOpen(true)
        } else {
          setCallState('error')
          setMfaError(true)
          toast.error(errorMessage)
        }
      }
      return
    }

    try {
      setCallState('loading')
      if (await authProvider.login(username, password, secret)) {
        setCallState('success')
        if (onLoginSuccess) {
          onLoginSuccess()
        } else {
          navigate('/', { replace: true })
        }
      } else if (onAdminLoginSuccess && authInstance.adminLogin) {
        // Regular login returned false (user not found as TRADER/GROUP_OWNER).
        // Try admin login in case this is a SUPER_ADMIN.
        await tryAdminLogin(secret)
      } else {
        setCallState('error')
        toast.error('Invalid username or password')
      }
    } catch (e) {
      const errorMessage = (e as Error).message

      if (errorMessage === 'MFA required') {
        setOpen(true)
        return
      } else if (errorMessage === 'MFA setup required') {
        setMfaSetupRequired(true)
        try {
          const qrUrl = await authInstance.requestMFA(username, password)
          setQrCodeUrl(qrUrl)
        } catch (_error) {
          setMfaError(true)
          toast.error('Failed to request MFA setup')
        }
        return
      } else {
        setCallState('error')
        setMfaError(true)
        toast.error(errorMessage)
      }
    }
  }

  const tryAdminLogin = async (secret?: string) => {
    if (!onAdminLoginSuccess || !authInstance.adminLogin) return
    setIsSuperAdminFlow(true)
    try {
      const adminData = await authInstance.adminLogin(username, password, secret)
      setCallState('success')
      onAdminLoginSuccess(adminData.token)
    } catch (adminError) {
      const adminErrorMessage = (adminError as Error).message
      if (adminErrorMessage === 'MFA required') {
        setOpen(true)
      } else if (adminErrorMessage === 'MFA setup required') {
        setMfaSetupRequired(true)
        try {
          const qrUrl = await authInstance.requestMFA(username, password)
          setQrCodeUrl(qrUrl)
        } catch (_error) {
          setMfaError(true)
          toast.error('Failed to request MFA setup')
        }
      } else {
        setCallState('error')
        setMfaError(true)
        toast.error('Invalid username or password')
      }
    }
  }

  const handleMfaSetup = async (secret: string) => {
    try {
      setMfaLoading(true)
      setMfaError(false)
      await authInstance.setupMFA(username, password, secret)
      toast.success('MFA setup successfully! Please login again.')
      // Reset state and show login form again
      setCallState('disabled')
      setMfaSetupRequired(false)
      setQrCodeUrl(null)
      setOpen(false)
      setMfaCode('')
    } catch (error) {
      setMfaError(true)
      toast.error((error as Error).message || 'Failed to setup MFA')
    } finally {
      setMfaLoading(false)
    }
  }

  const handleMfaConfirm = async (value: string) => {
    try {
      setMfaLoading(true)
      setMfaError(false)
      await handleSubmit(value)
    } catch (error) {
      setMfaError(true)
      toast.error((error as Error).message || 'Invalid MFA code')
    } finally {
      setMfaLoading(false)
    }
  }

  const handleMfaCodeChange = (value: string) => {
    setMfaCode(value)
    // Reset error state when user starts typing
    if (mfaError) {
      setMfaError(false)
    }
  }

  useEffect(() => {
    // Preserve error, in-flight and successful states until the login flow handles them.
    if (callState === 'error' || callState === 'loading' || callState === 'success') {
      return
    }

    if (username.length > 0 && password.length > 0) {
      if (callState !== 'action') {
        setCallState('action')
      }
    } else {
      if (callState !== 'disabled') {
        setCallState('disabled')
      }
    }
  }, [username, password, callState])

  const secretCode = useMemo(() => {
    return qrCodeUrl?.split('secret=')[1]
  }, [qrCodeUrl])

  const buttonVariant = useMemo(() => {
    switch (callState) {
      case 'action':
      case 'success':
        return 'square_active'
      case 'disabled':
      case 'loading':
        return 'square_disabled'
      case 'error':
        return 'square_error'
    }
  }, [callState])

  const LogoComponent = logo({})

  const checkingPasskey = open && !isSuperAdminFlow && authInstance.loginWithPasskey && authInstance.passkeyLoginAvailable && passkeyAvailable === null
  if (loginComplete || checkingPasskey) return <LoadingPage />

  return (
    <Modal LogoComponent={LogoComponent} className="relative">
      {variant === 'admin' && (
        <div className="absolute -top-14 left-0 h-[40px] flex items-center justify-center w-full md:bg-white rounded-2xl">
          <span className="text-main text-[16px] font-semibold border border-main px-8 md:border-none rounded-2xl">
            Admin Portal
          </span>
        </div>
      )}
      {/* Login Form */}
      {!open && !mfaSetupRequired ? (
        <div className="grid gap-6">
          <Header title={'Welcome back,'} />
          <SubHeader title={'Please enter your details to log-in.'} />
          <div className="grid gap-2">
            <Input
              id="username"
              type="text"
              autoComplete="username"
              placeholder="Username"
              required
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit()
                }
              }}
            />
          </div>
          <div className="grid gap-2">
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                placeholder="Password"
                className="pr-24"
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSubmit()
                  }
                }}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPassword(!showPassword)}
                  className="h-9 w-9 text-muted-foreground"
                >
                  {showPassword ? (
                    <EyeOff className="size-5 md:size-4" />
                  ) : (
                    <Eye className="size-5 md:size-4" />
                  )}
                </Button>
                <Button
                  type="submit"
                  variant={buttonVariant}
                  size="icon"
                  onClick={() => handleSubmit()}
                >
                  <div className={cn(callState === 'loading' && 'animate-spin')}>
                    {buttonActionIcon(callState)}
                  </div>
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : mfaSetupRequired && !mfaNext ? (
        // Setup MFA Form 1
        <div
          className="grid gap-6 overflow-hidden"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Header title="Setup Multi-factor Authentication" />

          <SubHeader title="Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)" />

          {qrCodeUrl && (
            <>
              <div className="mb-4 p-2 border-3 border-[#3A68FF] rounded-xl">
                <QRCode value={qrCodeUrl} fgColor="#3A68FF" size={151} />
              </div>

              <SubHeader title="Can't scan the QR Code ? Enter this code into your authenticator app instead." />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  navigator.clipboard.writeText(secretCode || '')
                  toast.success('Secret code copied to clipboard')
                }}
                className="flex justify-center items-center gap-2 w-full px-4"
              >
                <div className="font-bold text-xl flex-1 truncate break-words">{secretCode}</div>
                <Copy className="size-5 md:size-4" />
              </Button>
            </>
          )}

          <div className="flex justify-center w-full pt-[12px]">
            <Button
              variant="active"
              size="lg"
              onClick={() => {
                setMfaNext(true)
              }}
              className="h-10"
            >
              Next{' '}
              <div className="flex items-center justify-center w-[11px]">
                {buttonActionIcon('action')}
              </div>
            </Button>
          </div>
        </div>
      ) : mfaSetupRequired && mfaNext ? (
        // Setup MFA Form 2
        <div
          className="grid gap-6"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Header title="Confirm Code" />

          <SubHeader title="Once the authenticator app generates a confirmation code, enter it below." />

          <div className="relative flex justify-center items-center h-[60px] w-full">
            <div className="flex justify-center w-full absolute">
              <InputOTP
                value={mfaCode}
                onChange={(value) => {
                  handleMfaCodeChange(value)
                  if (value.length === 6) {
                    handleMfaSetup(value)
                  }
                }}
                length={6}
                disabled={mfaLoading}
                autoFocus={true}
                hasError={mfaError}
                name="mfa-setup-code"
                data-testid="mfa-setup-otp-input"
              />
            </div>
          </div>
          {mfaLoading && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              Setting up MFA...
            </div>
          )}
        </div>
      ) : (
        // MFA Form
        <div
          className="grid gap-6 relative"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Header title="Multi-factor authentication" />
          <SubHeader title="Enter the code from your authenticator app" />
          <div className="relative flex justify-center items-center h-[60px] w-full">
            <div className="flex justify-center w-full absolute">
              <InputOTP
                value={mfaCode}
                onChange={(value) => {
                  handleMfaCodeChange(value)
                  if (value.length === 6) {
                    handleMfaConfirm(value)
                  }
                }}
                length={6}
                disabled={mfaLoading}
                autoFocus={true}
                hasError={mfaError}
                name="mfa-code"
                data-testid="mfa-otp-input"
              />
            </div>
          </div>
          {mfaLoading && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              Verifying code...
            </div>
          )}
          {!isSuperAdminFlow && passkeyAvailable && authInstance.loginWithPasskey && (
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              disabled={mfaLoading}
              onClick={async () => {
                try {
                  setMfaLoading(true)
                  setMfaError(false)
                  const tokens = await authInstance.loginWithPasskey!(username, password)
                  const { role, userId } = authProvider.decodeToken(tokens.token)
                  authProvider.setAuth({ ...tokens, role, userId })
                  setCallState('success')
                  if (onLoginSuccess) {
                    onLoginSuccess()
                  } else {
                    navigate('/', { replace: true })
                  }
                } catch (error) {
                  setMfaError(true)
                  toast.error(passkeyError(error))
                } finally {
                  setMfaLoading(false)
                }
              }}
            >
              Verify with a passkey
            </Button>
          )}
        </div>
      )}
    </Modal>
  )
}
